import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { RoleBadge, ROLE_SUGGESTIONS } from "@/components/badges";
import { Trash2, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CompanyDialog } from "./CompanyDialog";
import { useConfirm } from "@/components/ConfirmDialog";

export function PartiesTab() {
  const { askConfirm, confirmDialog } = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { selectedId } = useProject();
  const [partyForm, setPartyForm] = useState({ company_id: "", role: "", can_view_all_progress: false, parent_party_id: "" });
  const [partyOpen, setPartyOpen] = useState(false);
  const [companyDialog, setCompanyDialog] = useState(null);

  const { data: companies = [] } = useQuery({
    queryKey: ["admin-companies"],
    queryFn: async () => (await api.get("/admin/companies")).data,
  });
  const { data: parties = [] } = useQuery({
    queryKey: ["parties", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/parties`)).data,
    enabled: !!selectedId,
  });

  const partyMut = useMutation({
    mutationFn: async () => (await api.post(`/admin/projects/${selectedId}/parties`, partyForm)).data,
    onSuccess: () => { toast.success(t("admin.toastPartyAssigned")); setPartyOpen(false); setPartyForm({ company_id: "", role: "", can_view_all_progress: false }); qc.invalidateQueries({ queryKey: ["parties", selectedId] }); },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const partyUpdateMut = useMutation({
    mutationFn: async ({ id, body }) => api.put(`/admin/parties/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["parties", selectedId] }),
    onError: (e) => toast.error(fmtErr(e)),
  });
  const partyDeleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/parties/${id}`),
    onSuccess: () => { toast.success(t("admin.toastPartyRemoved")); qc.invalidateQueries({ queryKey: ["parties", selectedId] }); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">{t("admin.partiesCard")}</CardTitle>
          <Button size="sm" onClick={() => setPartyOpen(true)} data-testid="admin-assign-party-button"><UserPlus className="mr-2 h-4 w-4" /> {t("admin.assignCompany")}</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader><TableRow><TableHead>{t("admin.thCompany")}</TableHead><TableHead>{t("admin.thRole")}</TableHead><TableHead>{t("admin.thParent")}</TableHead><TableHead>{t("admin.thSeesAll")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {parties.map((p) => (
                <TableRow key={p.id} data-testid={`admin-party-row-${p.id}`}>
                  <TableCell className="font-medium" title={p.company_legal_name}>
                    {p.company_name} {p.is_placeholder && <Badge variant="outline" className="ml-1 font-mono text-[10px] bg-slate-100">TBD</Badge>}
                    {p.company_legal_name && p.company_legal_name !== p.company_name && (
                      <div className="text-xs font-normal text-slate-400">{p.company_legal_name}</div>
                    )}
                  </TableCell>
                  <TableCell><RoleBadge role={p.role} /></TableCell>
                  <TableCell>
                    {(p.role || "").trim().toLowerCase() === "client" ? (
                      <span className="text-xs text-slate-400">{t("admin.rootMark")}</span>
                    ) : (
                      <Select value={p.parent_party_id || "none"} onValueChange={(v) => partyUpdateMut.mutate({ id: p.id, body: { parent_party_id: v === "none" ? "" : v } })}>
                        <SelectTrigger className="h-8 w-44 text-xs" data-testid={`admin-party-parent-${p.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none" data-testid={`admin-party-parent-none-${p.id}`}>{t("admin.noneTop")}</SelectItem>
                          {parties.filter((x) => x.id !== p.id).map((x) => (
                            <SelectItem key={x.id} value={x.id} data-testid={`admin-party-parent-opt-${p.id}-${x.id}`}>{x.company_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!!p.can_view_all_progress}
                      onCheckedChange={(v) => partyUpdateMut.mutate({ id: p.id, body: { can_view_all_progress: v } })}
                      data-testid={`admin-party-visibility-${p.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost" size="icon" className="text-rose-600"
                      onClick={() => askConfirm(t("admin.removePartyConfirm", { company: p.company_name, role: p.role }), () => partyDeleteMut.mutate(p.id))}
                      data-testid={`admin-party-delete-${p.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={partyOpen} onOpenChange={setPartyOpen}>
        <DialogContent data-testid="assign-party-dialog">
          <DialogHeader><DialogTitle>{t("admin.assignDialogTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>{t("admin.thCompany")}</Label>
                <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs text-sky-700" onClick={() => setCompanyDialog("new")} data-testid="party-new-company-button">
                  {t("admin.createNewCompany")}
                </Button>
              </div>
              <Select value={partyForm.company_id} onValueChange={(v) => setPartyForm({ ...partyForm, company_id: v })}>
                <SelectTrigger data-testid="party-company-select"><SelectValue placeholder={t("admin.selectCompany")} /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`party-company-${c.id}`}>
                      <span className="font-mono font-semibold">{c.code || c.name}</span>
                      {c.code && c.code !== c.name && <span className="ml-1 font-normal text-slate-500">· {c.name}</span>}
                      {c.is_placeholder ? " (TBD)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("admin.roleFreeText")}</Label>
              <Input list="role-suggestions" value={partyForm.role} onChange={(e) => setPartyForm({ ...partyForm, role: e.target.value })} placeholder={t("admin.rolePh")} data-testid="party-role-input" />
              <datalist id="role-suggestions">
                {ROLE_SUGGESTIONS.map((r) => <option key={r} value={r} />)}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <Label>Parent company (optional — organisation chart only)</Label>
              <Select value={partyForm.parent_party_id || "none"} onValueChange={(v) => setPartyForm({ ...partyForm, parent_party_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="party-parent-select"><SelectValue placeholder="None (top level)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="party-parent-none">— None (top level) —</SelectItem>
                  {parties.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`party-parent-${p.id}`}>{p.company_name} ({p.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Used only for the Project Members org chart. Client-role companies always stay at the root.</p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="seeall" checked={partyForm.can_view_all_progress} onCheckedChange={(v) => setPartyForm({ ...partyForm, can_view_all_progress: !!v })} data-testid="party-visibility-checkbox" />
              <Label htmlFor="seeall" className="font-normal">This party can see all parties' progress</Label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => partyMut.mutate()} disabled={!partyForm.company_id || !partyForm.role.trim() || partyMut.isPending} data-testid="assign-party-submit-button">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CompanyDialog dialog={companyDialog} onClose={() => setCompanyDialog(null)} onCreated={(id) => setPartyForm((f) => ({ ...f, company_id: id }))} />
      {confirmDialog}
    </>
  );
}
